import { useState } from 'react';
import { friendlyMessage } from "@/lib/errors";
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { authApi } from '@/lib/api/services/auth';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { AuthShell } from '@/components/AuthShell';

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [confirmError, setConfirmError] = useState('');
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const tokenError = token ? '' : 'This reset link is invalid or has expired.';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        let pErr = '';
        let cErr = '';
        if (!password) {
            pErr = 'Please enter a new password';
        } else if (password.length < 6) {
            pErr = 'Password must be at least 6 characters';
        }
        if (!confirmPassword) {
            cErr = 'Please confirm your new password';
        } else if (password && password !== confirmPassword) {
            cErr = 'Passwords do not match';
        }
        setPasswordError(pErr);
        setConfirmError(cErr);
        if (!token || pErr || cErr) return;

        setLoading(true);

        try {
            const result = await authApi.resetPassword({ token, password });

            if (result.error) {
                setPasswordError(result.error);
            } else {
                setSuccess(true);
                toast({ variant: "success",
                    title: "Password reset!",
                    description: "Your password has been reset successfully.",
                });
                setTimeout(() => {
                    navigate('/auth');
                }, 2000);
            }
        } catch (error: any) {
            setPasswordError(friendlyMessage(error, "We couldn't reset your password. Please try again."));
        } finally {
            setLoading(false);
        }
    };

    const newPasswordError = tokenError || passwordError;

    return (
        <AuthShell>
            <button
                type="button"
                aria-label="Back"
                onClick={() => navigate('/auth')}
                disabled={loading}
                className="mb-3 -ml-1 inline-flex h-9 w-fit items-center gap-1.5 rounded-lg px-1 text-sm font-medium text-gray-700 transition-colors hover:text-gray-900"
            >
                <ArrowLeft className="h-4 w-4" />
                Back
            </button>

            <h1 className="text-2xl font-bold text-gray-900">
                {success ? 'Password updated' : 'Reset password'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
                {success
                    ? 'Your password has been reset. Redirecting you to login...'
                    : 'Enter a new password for your account.'}
            </p>

            {success ? (
                <div className="mt-6 space-y-4">
                    <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
                        <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-green-500" />
                        <p className="text-sm text-gray-600">You can now log in with your new password.</p>
                    </div>
                    <Button
                        type="button"
                        className="w-full !h-12 font-bold text-[16px]"
                        onClick={() => navigate('/auth')}
                    >
                        Go to login
                    </Button>
                </div>
            ) : (
                <>
                    <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="password">New password</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Your new password"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        if (passwordError) setPasswordError('');
                                    }}
                                    aria-invalid={!!newPasswordError}
                                    className={cn('h-12 pr-10', newPasswordError && 'border-red-400 focus-visible:ring-red-400')}
                                    disabled={loading}
                                    autoFocus={!!token}
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                        <Eye className="h-4 w-4 text-muted-foreground" />
                                    )}
                                </Button>
                            </div>
                            {tokenError ? (
                                <p className="text-xs text-red-600">
                                    {tokenError}{' '}
                                    <Link to="/forgot-password" className="font-semibold underline">
                                        Request a new link
                                    </Link>
                                </p>
                            ) : passwordError ? (
                                <p className="text-xs text-red-600">{passwordError}</p>
                            ) : (
                                <p className="text-xs text-gray-500">Must be at least 6 characters.</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirm new password</Label>
                            <div className="relative">
                                <Input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    placeholder="Re-enter your new password"
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        if (confirmError) setConfirmError('');
                                    }}
                                    aria-invalid={!!confirmError}
                                    className={cn('h-12 pr-10', confirmError && 'border-red-400 focus-visible:ring-red-400')}
                                    disabled={loading}
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? (
                                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                                    ) : (
                                        <Eye className="h-4 w-4 text-muted-foreground" />
                                    )}
                                </Button>
                            </div>
                            {confirmError && <p className="text-xs text-red-600">{confirmError}</p>}
                        </div>

                        <Button
                            type="submit"
                            className="w-full !h-12 font-bold text-[16px]"
                            disabled={loading}
                        >
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {loading ? 'Resetting...' : 'Reset password'}
                        </Button>
                    </form>

                    <p className="mt-4 text-center text-sm text-gray-500">
                        Remembered it?{' '}
                        <button
                            type="button"
                            onClick={() => navigate('/auth')}
                            disabled={loading}
                            className="font-semibold text-[var(--secondary-color)] hover:underline"
                        >
                            Back to login
                        </button>
                    </p>
                </>
            )}

            <p className="mt-8 text-center text-xs text-gray-400">
                <a
                    href="https://wenext.ai/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                >
                    Privacy Policy
                </a>
                <span className="mx-2">•</span>
                <a
                    href="https://wenext.ai/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                >
                    Terms of Service
                </a>
            </p>
        </AuthShell>
    );
}
